/**
 * Explainers on the staff settings page: how the Category Profile is built and how the strategy is set up.
 * Figures in brackets are the defaults; most of them are settings on this page.
 */

export type ExplainerBlock = {
    heading: string;
    text?: string;
    bullets?: string[];
    table?: {columns: string[]; rows: string[][]};
};

/** `staffOnly`: about builds and market data, which only Mithra staff run (customer super users do not see it). */
export type Explainer = {key: string; title: string; intro: string; blocks: ExplainerBlock[]; staffOnly?: boolean};

export const EXPLAINERS: Explainer[] = [
    {
        key: 'build',
        staffOnly: true,
        title: 'How the Category Profile is built',
        intro: 'After a dataset is loaded and categorised, a build turns its spend lines into summary tables, so Explore '
            + 'Categories and the Category Profile never add up raw lines while someone waits.',
        blocks: [
            {
                heading: 'How to run it',
                bullets: [
                    'python manage.py build_category_facts --databag <id>: builds one dataset. Without --databag it builds '
                    + 'every enabled dataset. --skip-benchmarks builds only the facts.',
                    'Rebuild under Builds on this page does the same inside a web request, so use it only for small datasets.',
                    'Nothing schedules it yet: run it after every data delivery or re-categorisation.',
                ],
            },
            {
                heading: 'What it reads',
                text: 'The current categorisation: each line\'s category as reviewed (review revision 0), with deleted lines '
                    + 'left out. Every category level from L1 to L8 is covered.',
            },
            {
                heading: '1. Category facts',
                bullets: [
                    'One database query summarises every dated line per category, month, supplier, business unit, site, '
                    + 'cost centre and supplier country.',
                    'Each row holds spend, PO spend (spend under management), quantity, transactions and PO lines. A line '
                    + 'is a PO line when it has a PO number. The prior year is kept for year-on-year.',
                    'Quantity, unit, site, cost centre and PO number are read only when the dataset\'s column '
                    + 'configuration has them in use; without a column configuration all of them are read.',
                    'Feeds the Explore tree (totalled up to each level when requested), header figures, risk flags, the '
                    + 'Overview charts, year to date, document mix, the Suppliers tab and the Pareto.',
                ],
            },
            {
                heading: '2. Transaction lines',
                text: 'A slim copy of every dated line (category, date, amount, supplier, material, description, site, PO), '
                    + 'indexed by amount and by date, so the Transactions tab pages through hundreds of thousands of lines '
                    + 'in under a second.',
            },
            {
                heading: '3. Build record',
                text: 'When the build ran, the latest month with data (it sets every "last 12 months" window) and the lines '
                    + 'and spend without a date. The build time is part of the cache key, so cached figures refresh after '
                    + 'every rebuild. Steps 1 to 3 replace the dataset\'s data in one transaction: nobody sees a half build.',
            },
            {
                heading: '4. Internal benchmarks',
                bullets: [
                    'Window: the last months with data (12).',
                    'Items: lines are grouped by material code, else Material Harmonization cluster, else normalised '
                    + 'description within the leaf category. Each item records how it was matched.',
                    'Units: quantities are converted to a base unit (piece, kg, litre, m); unknown units and zero '
                    + 'quantities are left out.',
                    'Prices: prices more than 10× above or below the item\'s median are dropped as unit errors. Then a '
                    + 'volume-weighted price per site, business unit and supplier.',
                    'Best price: the volume-weighted P10 of site prices, among sites with at least 5% of the volume.',
                    'Saving: what each site overpays against the best price × its quantity, capped at 30% of the item\'s '
                    + 'spend.',
                    'Confidence 0–100 (match method, lines, sites, suppliers, unit coverage, price spread). Below 60 an '
                    + 'item is "indicative" and left out of totals.',
                    'Contract prices are added as a reference where a linked contract has a unit price for the item.',
                    'Stored per item, with prices per site, business unit and supplier, and monthly spend and quantity '
                    + 'for the price-paid index on Market index.',
                ],
            },
            {
                heading: '5. Savings-tracker mapping',
                text: 'Links the Savings Initiative Tracker\'s own L1–L4 categories to taxonomy categories by matching '
                    + 'names, so the header can show cost savings achieved. Only new matches are added; staff corrections '
                    + 'are kept.',
            },
            {
                heading: 'Not part of the build',
                bullets: [
                    'Market data: python manage.py ingest_market_data loads the Eurostat price indices, EU consumer prices '
                    + 'and ECB exchange rates, shared by every dataset. Re-running replaces months, never duplicates them.',
                    'Entered data is stored as entered: owner, team, requirements, notes, strategy plan, contract clauses, '
                    + 'price models, quotes, external signals.',
                    'Calculated when a page opens: driver correlations, should cost, the price monitor and external risk.',
                    'Settings marked "After rebuild" (benchmark parameters) apply at the next build; the '
                    + 'others apply immediately.',
                ],
            },
        ],
    },
    {
        key: 'strategy',
        title: 'How the strategy is set up',
        intro: 'The Strategy tab holds one category plan per category per dataset: the Stage 4 plan from Cordell & '
            + 'Thompson. Mithra drafts it from the category\'s data, the team edits it section by section, and it goes '
            + 'through a gateway approval with three sign-offs.',
        blocks: [
            {
                heading: 'Where the draft comes from',
                text: 'Calculated when the tab is first opened, and saved only at the first edit. It uses spend over the '
                    + 'period set under Strategy on this page (last 12 months, with the change on the year before, or all '
                    + 'data in the dataset), suppliers and the top supplier\'s share, the risk '
                    + 'flags, spend by supplier country, the five largest suppliers (share, PO share, contract), contracts, '
                    + 'owner and team roles, the RAQSCI requirements, and total spend across the dataset.',
            },
            {
                heading: 'Sections and how the draft fills them',
                table: {
                    columns: ['Section', 'Draft'],
                    rows: [
                        ['Plan details', 'Name, top 3 countries as coverage, scope from the taxonomy description, '
                            + 'influenceable spend, manager (team\'s category manager, else owner), sponsor (owner), '
                            + 'horizon FY2027–FY2029, quarterly review'],
                        ['Summary', 'Generated paragraph: spend and growth, suppliers, concentration, Kraljic quadrant, '
                            + 'targeted savings'],
                        ['Kraljic', 'Supply risk = top supplier\'s share. Profit impact = category\'s share of total '
                            + 'spend on a square-root scale (1% → 20, 4% → 40, 25% → 100). Quadrants split at 50'],
                        ['Supplier preferencing (top 5)', 'Attractiveness 50, +15 with a contract, +10 with 80%+ of spend '
                            + 'on POs. Account value = spend share × 2'],
                        ['Purchasing wheel', 'Up to 4 levers suited to the Kraljic quadrant'],
                        ['Power (top 3)', 'Our dependence = share × 1.2; their dependence starts at 30'],
                        ['PICK', '7 standard opportunities as a % of spend: should cost 7, specification standardisation '
                            + '5, price harmonisation 3.5, supplier consolidation 3 (1.5 without tail spend), index-linked '
                            + 'pricing 2.5 (avoidance), demand management 2, sustainability (value added). Payoff 1–5 from '
                            + 'the %, default difficulty per item'],
                        ['Option appraisal', 'RAQSCI weights R20 A20 Q15 S10 C25 I10; three options pre-scored by '
                            + 'quadrant. Score = Σ(weight × score) ÷ Σweights × 20'],
                        ['Strategy cube', '3–4 moves suggested for the quadrant'],
                        ['Roadmap', 'Implement starts Q1 for 3 quarters, Challenge Q4 for 5, Possible Q7 for 4, Kill '
                            + 'left off; plus supplier relationship programme and benefits tracking'],
                        ['Targets, actions, risks', 'Targets from flags and requirements; five dated actions; risks '
                            + 'from flags plus generic ones'],
                        ['Inputs and Stage 5', 'Ticks the team sets'],
                    ],
                },
            },
            {
                heading: 'Calculated live from the plan',
                bullets: [
                    'Quadrants and reads: Kraljic; each supplier\'s read from Kraljic × preferencing (16 combinations); '
                    + 'power reads.',
                    'Options: PICK totals per quadrant; appraisal scores, preferred option and a warning when weights do '
                    + 'not add up to 100%; the chosen levers.',
                    'Benefits: financial savings (Implement and Challenge), cost avoidance, value as % of influenceable spend.',
                    'Eight quality checks: 4+ requirements, roadmap reaches year 3, 3+ team roles, value 20%+ of '
                    + 'influenceable spend, 2+ non-financial targets, inputs 70%+ ready, approved at the gateway, review '
                    + 'cadence set.',
                    'Input readiness for 10 inputs (ready, partial, missing), and Stage 5 readiness: gateway and action '
                    + 'planning automatic; business acceptance, change management and benefits plan ticked by the team.',
                ],
            },
            {
                heading: 'Saving and approval',
                bullets: [
                    'Customer users and super users edit; read-only users cannot. Each section shows Drafted or Edited.',
                    'Every save raises the plan version; a save on an older version is refused ("reload").',
                    'Draft → Awaiting sign-off → Approved or Returned. Submit from Draft or Returned; unmet quality checks '
                    + 'are listed, not blocking. Locked while awaiting; the submitter or a super user can withdraw.',
                    'Each submission is a "Category Plan" request on the Approval page, where approvers sign off; the '
                    + 'Strategy tab links there. The request is approved when all three sign, and closed when the plan is '
                    + 'returned or withdrawn. A resubmission opens a new request.',
                    'Approvers are chosen under Strategy on this page (Plan approvers): any of category sponsor, '
                    + 'finance business partner and executive steering committee (all three by default). Each needs a '
                    + 'super user or Mithra staff (the submitter may sign too); one person fills one slot, and returning '
                    + 'needs a comment. Any return → Returned; all chosen approvers signed → Approved.',
                    'Editing after approval reopens the plan as Draft for a new sign-off. Every step is in the plan\'s '
                    + 'activity and the audit log, and triggers a notification.',
                ],
            },
        ],
    },
];
